import { useEffect, useState } from 'react';
import useKey from 'react-use/lib/useKey';
import styled from 'styled-components';

import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import PlayerDetails from '@sorare/gameplay/src/components/player/PlayerDetails';

import { PlayerGameScore } from './PlayerGameScore';

const ContentWrapper = styled.div`
  background: var(--c-neutral-100);
  & > * {
    min-height: 100vh;
  }
  @media ${tabletAndAbove} {
    width: var(--layout-dialog-width);
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  playerSlug: string | undefined;
  cardSlug?: string;
  scoreId?: string;
  withBackdropClick?: boolean;
  showUpsell?: boolean;
};

export const PlayerDetailsDrawer = ({
  open,
  onClose,
  playerSlug,
  cardSlug,
  scoreId,
  withBackdropClick,
  showUpsell,
}: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const [tabData, setTabData] = useState<{
    tab: 'playerDetails' | 'gameScore';
    scoreId?: string;
  }>({ tab: 'playerDetails' });

  useEffect(() => {
    if (scoreId) {
      setTabData({ tab: 'gameScore', scoreId });
    } else {
      setTabData({ tab: 'playerDetails' });
    }
  }, [scoreId, playerSlug]);

  useKey('Escape', onClose);

  return (
    <Drawer
      open={open}
      side={isTabletOrAbove ? 'right' : 'bottom'}
      {...(withBackdropClick && { onBackdropClick: onClose })}
    >
      <ContentWrapper>
        {playerSlug && tabData.tab === 'playerDetails' && (
          <PlayerDetails
            playerSlug={playerSlug}
            cardSlug={cardSlug}
            onClose={onClose}
            onScoreSelect={selectedScoreId => {
              setTabData({ tab: 'gameScore', scoreId: selectedScoreId });
            }}
          />
        )}
        {playerSlug && tabData.tab === 'gameScore' && tabData.scoreId && (
          <PlayerGameScore
            scoreId={tabData.scoreId}
            onClose={onClose}
            onBack={() => {
              setTabData({ tab: 'playerDetails' });
            }}
            showUpsell={showUpsell}
          />
        )}
      </ContentWrapper>
    </Drawer>
  );
};
